import * as _ from 'lodash';
import { ActionContext } from 'vuex';
import InformationPageEntry from '@/enums/InformationPageEntry';
import SettingsPageEntry from '@/enums/SettingsPageEntry';
import CategoryBuildingStructurePage from '@/enums/CategoryBuildingStructurePage';
import Device from '@/interfaces/Device';
import BuildingViewVariant from '@/enums/BuildingViewVariant';
import { Organization } from '@/store/modules/organization';
import Vue from 'vue';
import { TranslateFunction } from '@/helpers/dataValidator';
import { configureSentryScopeUser } from '@/helpers/sentryHelpers';
import { SelectFloorAddress } from '@/store/interfaces/SelectFloorAddress';
import { AuthState } from "@/store/modules/auth";

export enum UsersRole {
  Owner = 'Owner',
  Admin = 'Admin',
  Agent = 'Agent',
}
export const allUsersRoles = [
  UsersRole.Agent,
  UsersRole.Admin,
];
export function getRoleName(role: UsersRole, translateFunction: TranslateFunction): string {
  switch (role) {
    case UsersRole.Owner:
      return translateFunction('OrganizationDetails_OwnerRole')!;
    case UsersRole.Admin:
      return translateFunction('OrganizationDetails_AdminRole')!;
    case UsersRole.Agent:
      return translateFunction('OrganizationDetails_AgentRole')!;
  }
}

export type User = {
  id: string;
  name: string;
  email: string;
  gravatar: null;
  organizationId: string | undefined;
  isOrganizationAdmin: boolean;
  role: UsersRole | undefined;
  creationDate: string;
  updateDate: string;
};

export type DeviceIdAndType = {
  type: string | null;
  id: string | null;
};

export type Building = {
  id: number;
  name: string;
};

let checkQueryTimer: any = undefined;

export interface SetupCommissioningSessionSalt {
  floorId: string;
  salt: string;
}

export enum DeviceEditingPageTab {
  General = 'General',
  PowerConsumption = 'PowerConsumption',
}

export interface DeviceEditingPage {
  __typename: 'DeviceEditingPage';
  device: Device;
  tab: DeviceEditingPageTab;
}

export enum AppCommonSettingsPage {
  PowerConsumption = 'PowerConsumption',
}

export interface CommonSettingsPage {
  __typename: 'CommonSettingsPage';
  page: AppCommonSettingsPage;
}

export type AppPage = DeviceEditingPage | CommonSettingsPage;

export type UserState = {
  currentUser: User | { loading: false };
  currentBuildingId: string | undefined;
  currentFloorId: string | undefined;
  currentFloorIsLoading: boolean;
  currentSelectedRoomId: number | undefined;
  currentSelectedDevice: DeviceIdAndType | undefined;
  currentSelectedEntryInformationPage: InformationPageEntry;
  currentSelectedEntrySettingsPage: SettingsPageEntry;
  currentSelectedCategoryBuildingStructurePage: CategoryBuildingStructurePage;
  currentSelectedBuildingViewVariant: BuildingViewVariant;
  pagesStack: AppPage[];
  checkQuery: number;
  setupCommissioningSessions: SetupCommissioningSessionSalt[];
  isScenarioEnabled: boolean;
  isCookieBannerShow: boolean;
};
export default {
  state: {
    currentUser: {
      name: undefined,
      email: undefined,
      id: undefined,
      gravatar: undefined,
      organizationId: undefined,
      loading: false,
      creationDate: undefined,
      updateDate: undefined,
    },
    currentBuildingId: undefined,
    currentFloorId: undefined,
    currentFloorIsLoading: false,
    currentSelectedRoomId: undefined,
    currentSelectedDevice: undefined,
    currentSelectedEntryInformationPage: 0,
    currentSelectedEntrySettingsPage: 0,
    currentSelectedCategoryBuildingStructurePage: 0,
    currentSelectedBuildingViewVariant: BuildingViewVariant.ListView,
    pagesStack: [],
    checkQuery: 0,
    setupCommissioningSessions: [],
    isScenarioEnabled: true,
    isCookieBannerShow: true
  },
  getters: {
    currentBuildingId(state: UserState) {
      return state.currentBuildingId;
    },
    currentFloorId(state: UserState) {
      return state.currentFloorId;
    },
    currentOrganizationId(state: UserState) {
      return (<any>state.currentUser).organizationId;
    },
    currentUserId(state: UserState) {
      return (<any>state.currentUser).id;
    },
    currentUserOrganizationAndBuilding(state: UserState) {
      return {
        userId: (<any>state.currentUser).id,
        buildingId: state.currentBuildingId,
        organizationId: (<any>state.currentUser).organizationId,
      };
    },
    currentEditingDeviceBuildingStructurePage(state: UserState): DeviceEditingPage | undefined {
      const lastEditingDevicePage = <DeviceEditingPage | undefined>_.findLast(
        state.pagesStack,
        (page: AppPage) => page.__typename == 'DeviceEditingPage',
      );
      return lastEditingDevicePage;
    },
    isScenarioEnabled(state: UserState) {
      return state.isScenarioEnabled;
    },
    isCookieBannerShow(state: UserState) {
      return state.isCookieBannerShow;
    }
  },
  actions: {
    acceptedInvitation(
      context: ActionContext<AuthState, any>,
      { user }: {user: User}
    ) {
      context.commit('acceptInvitationSucceeded', user);
    },
    dropCurrentOrganization(
      context: ActionContext<UserState, any>,
    ) {
      context.commit('dropCurrentOrganization');
    },
    dropCurrentBuilding(
      context: ActionContext<UserState, any>,
    ) {
      context.commit('dropCurrentBuilding');
    },
    dropCurrentUser(
      context: ActionContext<UserState, any>,
    ) {
      context.commit('dropCurrentUser');
    },
    organizationAdded(
      context: ActionContext<UserState, any>,
      organization: Organization,
    ) {
      context.commit('organizationAdded', organization);
    },
    changeCurrentFloor(
      context: ActionContext<UserState, any>,
      data: SelectFloorAddress,
    ) {
      context.commit('changeCurrentFloor', data);
      context.commit('cleanSelectedItemsOnBuildingChange');
    },
    startCurrentFloorLoading(context: ActionContext<UserState, any>) {
      context.commit('startCurrentFloorLoading');
    },
    stopCurrentFloorLoading(context: ActionContext<UserState, any>) {
      context.commit('stopCurrentFloorLoading');
    },
    setIsScenarioEnabled(
      context: ActionContext<UserState, any>,
      isScenarioEnabled: boolean
    ) {
      context.commit('setIsScenarioEnabled', isScenarioEnabled);
    },
    selectCurrentRoom(
      context: ActionContext<UserState, any>,
      { roomId }: { roomId: string },
    ) {
      context.commit('selectCurrentRoom', roomId);
    },
    selectOrganization(
      context: ActionContext<UserState, any>,
      { organizationId }: { organizationId: string },
    ) {
      context.commit('selectOrganization', organizationId);
    },
    selectCurrentDevice(
      context: ActionContext<UserState, any>,
      { deviceId, deviceType }: { deviceId: number; deviceType: string },
    ) {
      context.commit('selectCurrentDevice', { deviceId, deviceType });
    },
    selectCurrentEntryInformationPage(
      context: ActionContext<UserState, any>,
      { entry }: { entry: InformationPageEntry },
    ) {
      context.commit('selectCurrentEntryInformationPage', entry);
    },
    selectCurrentEntrySettingsPage(
      context: ActionContext<UserState, any>,
      { entry }: { entry: SettingsPageEntry },
    ) {
      context.commit('selectCurrentEntrySettingsPage', entry);
    },
    pushAppSettingsPageToPagesStack(
      context: ActionContext<UserState, any>,
      { appSettingsPage }: { appSettingsPage: AppCommonSettingsPage },
    ) {
      context.commit(
        'pushAppSettingsPageToPagesStack',
        appSettingsPage,
      );
    },
    selectCurrentSelectedCategoryBuildingStructurePage(
      context: ActionContext<UserState, any>,
      { section }: { section: CategoryBuildingStructurePage },
    ) {
      context.commit(
        'selectCurrentSelectedCategoryBuildingStructurePage',
        section,
      );
    },
    goBackFromCurrentEditingDeviceBuildingStructurePage(
      context: ActionContext<UserState, any>,
    ) {
      context.commit('goBackFromCurrentEditingDeviceBuildingStructurePage');
    },
    selectCurrentEditingDeviceBuildingStructurePage(
      context: ActionContext<UserState, any>,
      data: { device: Device; tab: DeviceEditingPageTab | undefined; },
    ) {
      context.commit('selectCurrentEditingDeviceBuildingStructurePage', data);
    },
    deselectCurrentDevice(
      context: ActionContext<UserState, any>,
    ) {
      context.commit('deselectCurrentDevice');
    },
    changeCurrentBuildingViewVariant(
      context: ActionContext<UserState, any>,
      { variant }: { variant: BuildingViewVariant },
    ) {
      context.commit('changeCurrentBuildingViewVariant', variant);
    },
    setupCommissioningSessionStarted(
      context: ActionContext<UserState, any>,
      salt: SetupCommissioningSessionSalt,
    ) {
      context.commit('setupCommissioningSessionStarted', salt);
    },
    setupCommissioningSessionStopped(
      context: ActionContext<UserState, any>,
      floorId: string,
    ) {
      context.commit('setupCommissioningSessionStopped', floorId);
    },
    setIsCookieBannerShow(
      context: ActionContext<UserState, any>,
      isCookieBannerShow: boolean
    ) {
      context.commit('setIsCookieBannerShow', isCookieBannerShow);
    },
  },
  mutations: {
    acceptInvitationSucceeded(state: UserState, user: User) {
      assignUserToState(state, user);
    },
    changeCurrentFloor(
      state: UserState,
      data: SelectFloorAddress,
    ) {
      state.currentBuildingId = data.newBuildingId;
      state.currentFloorId = data.newFloorId;
    },
    startCurrentFloorLoading(state: UserState) {
      state.currentFloorIsLoading = true;
    },
    stopCurrentFloorLoading(state: UserState) {
      state.currentFloorIsLoading = false;
    },
    setIsScenarioEnabled(
      state: UserState,
      isScenarioEnabled: boolean
    ) {
      state.isScenarioEnabled = isScenarioEnabled;
    },
    dropCurrentOrganization(
      state: UserState,
    ) {
      state.currentBuildingId = undefined;
      state.currentFloorId = undefined;
      state.currentSelectedRoomId = undefined;
      state.currentSelectedDevice = undefined;
      state.pagesStack = [];

      state.currentUser = _.assign(
        state.currentUser,
        {
          /* tslint:disable */ organizationId: null, /* tslint:enable */
        },
      );
    },
    dropCurrentBuilding(
      state: UserState,
    ) {
      state.currentBuildingId = undefined;
      state.currentFloorId = undefined;
      state.currentSelectedRoomId = undefined;
      state.currentSelectedDevice = undefined;
      state.pagesStack = [];
    },
    dropCurrentUser(
      state: UserState,
    ) {
      state.currentUser = {
        name: undefined,
        email: undefined,
        id: undefined,
        gravatar: undefined,
        organizationId: undefined,
        loading: false,
        creationDate: undefined,
        updateDate: undefined,
      };
      configureSentryScopeUser({
        loading: false,
      });
    },
    signInSucceeded(state: UserState, user: User) {
      assignUserToState(state, user);
    },
    signInFailed(state: UserState) {
      assignUserToState(state, state.currentUser);
    },
    signUpSucceeded(state: UserState, user: User) {
      assignUserToState(state, user);
    },
    signUpFailed(state: UserState) {
      assignUserToState(state, state.currentUser);
    },
    organizationAdded(state: UserState, organization: Organization) {
      Vue.set(state.currentUser, 'organizationId', organization.id);
    },
    logoutCompleted(state: UserState) {
      state.currentUser = _.assign(state.currentUser, {
        name: undefined,
        email: undefined,
        id: undefined,
        gravatar: undefined,
        creationDate: undefined,
        updateDate: undefined,
        organization: undefined,
      });
      state.setupCommissioningSessions = [];
    },
    selectCurrentEntryInformationPage(
      state: UserState,
      entry: InformationPageEntry,
    ) {
      state.currentSelectedEntryInformationPage = entry;
    },
    selectCurrentEntrySettingsPage(state: UserState, entry: SettingsPageEntry) {
      state.currentSelectedEntrySettingsPage = entry;
    },
    pushAppSettingsPageToPagesStack(
      state: UserState,
      appSettingsPage: AppCommonSettingsPage,
    ) {
      state.pagesStack.push({
        __typename: 'CommonSettingsPage',
        page: appSettingsPage,
      });
    },
    selectCurrentSelectedCategoryBuildingStructurePage(
      state: UserState,
      section: CategoryBuildingStructurePage,
    ) {
      state.currentSelectedCategoryBuildingStructurePage = section;
    },
    selectCurrentEditingDeviceBuildingStructurePage(
      state: UserState,
      { device, tab, }: { device: Device; tab: DeviceEditingPageTab | undefined; },
    ) {
      const tabToSelect = (tab != undefined)
        ? tab
        : DeviceEditingPageTab.General;
      state.pagesStack.push({
        __typename: 'DeviceEditingPage',
        device: device,
        tab: tabToSelect,
      });
    },
    goBackFromCurrentEditingDeviceBuildingStructurePage(state: UserState) {
      state.pagesStack = _.dropRight(
        state.pagesStack,
      );
      const pageToOpen = _.last(state.pagesStack);
      if (pageToOpen == undefined) {
        return;
      }
      switch (pageToOpen.__typename) {
        case 'CommonSettingsPage':
          goToCommonSettingsPage(state, pageToOpen.page);
          state.pagesStack = _.dropRight(
            state.pagesStack,
          );
          break;
        case 'DeviceEditingPage':
          break;
      }
    },
    selectCurrentRoom(state: UserState, currentSelectedRoomId: number) {
      state.currentSelectedRoomId = currentSelectedRoomId;
    },
    selectOrganization(state: UserState, organizationId: string) {
      state.currentBuildingId = undefined;
      state.currentFloorId = undefined;
      state.currentSelectedRoomId = undefined;
      state.currentSelectedDevice = undefined;
      state.pagesStack = [];
      state.currentUser = _.assign(
        state.currentUser,
        {
          organizationId,
        },
      );
    },
    selectCurrentDevice(
      state: UserState,
      {
        deviceId,
        deviceType,
      }: {
        deviceId: string;
        deviceType: string;
      },
    ) {
      if (
        deviceType == 'Light' ||
        deviceType == 'Group' ||
        deviceType == 'Room' ||
        deviceType == 'Accessory'
      ) {
        state.currentSelectedDevice = {
          type: deviceType,
          id: deviceId,
        };
      }
    },
    deselectCurrentDevice(state: UserState) {
      state.currentSelectedDevice = undefined;
      state.currentSelectedRoomId = undefined;
    },
    checkQuery(state: UserState) {
      state.checkQuery = state.checkQuery + 1;
    },
    cleanSelectedItemsOnBuildingChange(state: UserState) {
      state.pagesStack = [];
      state.currentSelectedDevice = undefined;
      state.currentSelectedRoomId = undefined;
    },
    changeCurrentBuildingViewVariant(
      state: UserState,
      variant: BuildingViewVariant,
    ) {
      state.currentSelectedBuildingViewVariant = variant;
    },
    setupCommissioningSessionStarted(
      state: UserState,
      salt: SetupCommissioningSessionSalt,
    ) {
      state.setupCommissioningSessions = _(state.setupCommissioningSessions)
        .filter((session: SetupCommissioningSessionSalt) => session.floorId != salt.floorId)
        .concat(salt)
        .value();
    },
    setupCommissioningSessionStopped(
      state: UserState,
      floorId: string,
    ) {
      state.setupCommissioningSessions = _(state.setupCommissioningSessions)
        .filter((session: SetupCommissioningSessionSalt) => session.floorId != floorId)
        .value();
    },
    setIsCookieBannerShow(
      state: UserState,
      isCookieBannerShow: boolean
    ) {
      state.isCookieBannerShow = isCookieBannerShow;
    },
  },
};

function assignUserToState(state: UserState, user: User | { loading: false }) {
  state.currentUser = _.assign(state.currentUser, user);
}

function goToCommonSettingsPage(state: UserState, page: AppCommonSettingsPage) {
  state.currentSelectedEntrySettingsPage = commonSettingsPageToPageEntry(page);
}

function commonSettingsPageToPageEntry(page: AppCommonSettingsPage): SettingsPageEntry {
  switch (page) {
    case AppCommonSettingsPage.PowerConsumption:
      return SettingsPageEntry.EnergyMonitoring;
  }
}
